import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Linkedin, Settings, AlertCircle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useLinkedInStatus } from '@/hooks/use-linkedin-status'

type LinkedInRequiredGateProps = {
  /** Page name shown in the empty state, e.g. "Leads" or "Inbox". */
  readonly featureName: string
  /** One-line description of why this page needs LinkedIn. */
  readonly description: string
  /** The actual page UI to render once a LinkedIn account is connected. */
  readonly children: ReactNode
}

/**
 * Wraps Leads / Campaigns / Content / Inbox pages so a user without a
 * connected LinkedIn account sees a friendly "Connect LinkedIn first"
 * panel instead of a broken empty module that would silently 401 / 404
 * every API call.
 *
 * Three render states based on the shared useLinkedInStatus query:
 *   - loading        → small spinner card, prevents flash of either state
 *   - not_connected  → empty state with a CTA to /settings
 *   - reconnect_needed → similar but with a warning tone (account exists
 *                        but needs re-auth — e.g. Unipile session expired)
 *   - connected      → renders children unchanged
 *
 * Failure to fetch (network / auth blip) renders children — fail-open
 * so a transient API hiccup doesn't lock the user out of pages they
 * could legitimately use.
 */
export function LinkedInRequiredGate({
  featureName,
  description,
  children,
}: LinkedInRequiredGateProps) {
  const { data, isLoading, isError } = useLinkedInStatus()

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-8rem)] items-center justify-center p-6">
        <div className="flex items-center gap-2 text-sm text-[var(--color-silver)]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking LinkedIn connection…
        </div>
      </div>
    )
  }

  if (isError) {
    // Fail-open — don't block the user just because the status check blipped.
    return <>{children}</>
  }

  const status = data?.status ?? 'not_connected'

  if (status === 'connected') {
    return <>{children}</>
  }

  const reconnectNeeded = status === 'reconnect_needed'

  return (
    <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center p-6">
      <Card className="group relative w-full max-w-lg overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[var(--color-cobalt)]/15 blur-3xl"
        />
        <CardContent className="relative flex flex-col items-center gap-4 p-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[rgba(28,63,255,0.12)] ring-1 ring-[rgba(28,63,255,0.25)]">
            {reconnectNeeded ? (
              <AlertCircle className="h-6 w-6 text-amber-300" />
            ) : (
              <Linkedin className="h-6 w-6 text-[var(--color-cobalt-bright)]" />
            )}
          </div>

          <div className="space-y-1.5">
            <p className="eyebrow text-[var(--color-cyan)]">// LINKEDIN REQUIRED</p>
            <h2 className="text-2xl font-bold tracking-tight text-[var(--color-silver-bright)]">
              {reconnectNeeded
                ? `Reconnect LinkedIn to use ${featureName}`
                : `Connect LinkedIn to use ${featureName}`}
            </h2>
            <p className="text-sm leading-relaxed text-[var(--color-silver)]">
              {description}
            </p>
          </div>

          {reconnectNeeded && data?.account?.name && (
            <p className="text-xs text-amber-300/90">
              Account <span className="font-semibold">{data.account.name}</span> needs to
              be re-authenticated.
            </p>
          )}

          <Button asChild className="mt-2 gap-1.5">
            <Link to="/settings">
              <Settings className="h-4 w-4" />
              {reconnectNeeded ? 'Reconnect in Settings' : 'Connect in Settings'}
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
